/* eslint-disable no-await-in-loop, no-restricted-syntax */
import { buildMarkdown, parseMarkdown, serializeSrc } from 'services/imports/markdownContract';
import type { ImportTarget, LineSource, MarkdownLine } from 'services/imports/markdownContract';
import type { ImportPage } from 'services/imports/fileToPages';
import type { OcrLine } from 'services/imports/runOcr';
import { buildPagePrompt } from 'services/imports/buildPagePrompt';

/** OCR lines keyed by `ImportPage.id`. Only `index` (L<n>) reaches the model; `id` and `bbox` stay app-side. */
export type PageLines = ReadonlyMap<string, readonly OcrLine[]>;

export type PageNotice = { pageId: string; message: string };

export type ExtractMarkdownInput = {
  pages: readonly ImportPage[];
  lines: PageLines;
  extractText: (prompt: string) => Promise<string>;
  target: ImportTarget | null;
};

export type ExtractMarkdownResult = {
  markdown: string;
  lines: MarkdownLine[];
  notices: PageNotice[];
  errors: PageNotice[];
};

export type ResolvedSource = { page: ImportPage; line: OcrLine };

const toErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/** One output line is parsed alone, so a model heading cannot change the section of the others. */
const parseOutputLine = (raw: string): MarkdownLine => {
  const [parsed] = parseMarkdown(`## Data\n${raw}`).lines;
  return parsed ?? { lineNo: 0, key: '', value: raw.trim(), src: null, status: 'malformed', raw };
};

/**
 * The model's file and page are never trusted: the source is forced to the page being processed.
 * A line citing an OCR index that is not on the page keeps its (forced) source so the
 * screen can flag it as unverified; `unverified` is applied there, since it does not survive the markdown.
 */
const verifyLine = (line: MarkdownLine, page: ImportPage, indices: ReadonlySet<string>): MarkdownLine => {
  if (line.src === null) return line;
  const src = { file: page.fileName, page: page.pageNumber, line: line.src.line };
  if (!indices.has(line.src.line)) return { ...line, src, status: 'unverified' };
  return { ...line, src };
};

const FENCE_RE = /^`{3,}\s*[A-Za-z]*$/;
const COMMENT_ONLY_RE = /^<!--.*-->$/s;
const ALT_BULLET_RE = /^[*\u2022]\s+/;
const BARE_KEY_RE = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*\s*:/;

const COMPACT_SRC_RE = /\s@(L[1-9][0-9]*)\s*$/;

/** A trailing ` @L<n>` becomes the full src comment, built by the app from the page being processed. */
const expandCompactSrc = (text: string, page: ImportPage): string => {
  const match = COMPACT_SRC_RE.exec(text);
  if (!match) return text;
  const src = serializeSrc({ file: page.fileName, page: page.pageNumber, line: match[1] });
  return `${text.slice(0, match.index)} <!-- src: ${src} -->`;
};

/** Tolerates common model drift (missing/alternate bullet, fences, comment-only lines). Anything else is left as-is so it stays malformed. */
const normalizeOutputLine = (raw: string, page: ImportPage): string | null => {
  const text = raw.trim();
  if (text === '' || FENCE_RE.test(text) || COMMENT_ONLY_RE.test(text)) return null;
  if (ALT_BULLET_RE.test(text)) return expandCompactSrc(text.replace(ALT_BULLET_RE, '- '), page);
  return expandCompactSrc(BARE_KEY_RE.test(text) ? `- ${text}` : text, page);
};

export const parsePageOutput = (output: string, page: ImportPage, pageLines: readonly OcrLine[]): MarkdownLine[] => {
  const indices = new Set(pageLines.map((line) => line.index));
  return output
    .split(/\r\n|\n|\r/)
    .map((raw) => normalizeOutputLine(raw, page))
    .filter((raw): raw is string => raw !== null)
    .map(parseOutputLine)
    .map((line) => verifyLine(line, page, indices));
};

/** A line that would start a section when re-parsed is prefixed so it stays text. */
const safeMalformedText = (raw: string): string => {
  const text = raw.trim();
  return /^#{1,2}\s/.test(text) ? `- ${text}` : text;
};

/**
 * Malformed lines cannot pass through buildMarkdown (it takes only key/value/src),
 * so they are appended after the data lines, still inside the Data section.
 */
const buildResultMarkdown = (
  pages: readonly ImportPage[],
  lines: readonly MarkdownLine[],
  target: ImportTarget | null
): string => {
  const name = Array.from(new Set(pages.map((page) => page.fileName))).join(', ');
  const dataLines = lines
    .filter((line) => line.status !== 'malformed')
    .map((line) => ({ key: line.key, value: line.value, src: line.src }));
  const malformedText = lines
    .filter((line) => line.status === 'malformed')
    .map((line) => safeMalformedText(line.raw));
  const base = buildMarkdown({ name, target, lines: dataLines });
  return malformedText.length === 0 ? base : `${base}${malformedText.join('\n')}\n`;
};

/**
 * One model call per page, sequential. A page with no OCR lines or a failed
 * page is skipped without a call. A model error on one page goes to `errors`
 * and the loop continues. `lineNo` in `lines` is the ordinal in the result,
 * not a line number in `markdown`.
 */
export const extractMarkdown = async ({ pages, lines, extractText, target }: ExtractMarkdownInput): Promise<ExtractMarkdownResult> => {
  const notices: PageNotice[] = [];
  const errors: PageNotice[] = [];
  const extracted: MarkdownLine[] = [];

  for (const page of pages) {
    const pageLines = lines.get(page.id) ?? [];
    if (page.failed || pageLines.length === 0) {
      notices.push({ pageId: page.id, message: page.failed ?? 'No OCR lines on this page; skipped' });
    } else {
      try {
        const prompt = buildPagePrompt({ fileName: page.fileName, pageNumber: page.pageNumber, lines: pageLines });
        const output = await extractText(prompt);
        extracted.push(...parsePageOutput(output, page, pageLines));
      } catch (error) {
        errors.push({ pageId: page.id, message: toErrorMessage(error) });
      }
    }
  }

  return {
    markdown: buildResultMarkdown(pages, extracted, target),
    lines: extracted.map((line, index) => ({ ...line, lineNo: index + 1 })),
    notices,
    errors,
  };
};

/** Maps a verified `{file, page, line}` source to its page and OCR line. Null when nothing matches. */
export const resolveSource = (
  src: LineSource,
  pages: readonly ImportPage[],
  linesByPage: PageLines
): ResolvedSource | null => {
  const page = pages.find((candidate) => candidate.fileName === src.file && candidate.pageNumber === src.page);
  if (!page) return null;
  const line = linesByPage.get(page.id)?.find((candidate) => candidate.index === src.line);
  return line ? { page, line } : null;
};
